import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  VENTAS_POR_HORA_TITULO_DEFAULT,
  applyVentasPorHoraColumnLabelsFromApi,
  formatVentasPorHoraCell,
  mapApiVentasPorHoraTotalesToNormIds,
  mergeVentasPorHoraEncabezado,
  mergeVentasPorHoraFootTotals,
  resolveVentasPorHoraColumns,
  rowToVentasPorHoraNorm,
  sumVentasPorHoraTotals,
  withVentasPorHoraPeriodoLine,
} from './ventasPorHoraReportModel';
import {
  columnWidthsProportionalMm,
  drawEncabezadoReportePdf,
  pdfTableInnerWidthMm,
} from './reporteEncabezadoPdf';

const TABLE_HEAD_BG = [15, 47, 89];
const TABLE_HEAD_TEXT = [255, 255, 255];

const fmtPdf = (spec, value) => {
  const s = formatVentasPorHoraCell(spec, value);
  return s === '—' ? '—' : s;
};

/**
 * @param {object[]} rawRows
 * @param {{
 *   fechaInicio?: string,
 *   fechaFin?: string,
 *   cajaInicio?: number,
 *   cajaFin?: number,
 *   turnoInicio?: number,
 *   turnoFin?: number,
 *   fileBase?: string,
 *   encabezado?: object|null,
 *   encabezadoFallback?: object,
 *   reporteTitulo?: string,
 *   apiTotales?: object|null,
 *   columnas?: Record<string, string>|null,
 * }} [options]
 */
export function downloadVentasPorHoraPdf(rawRows, options = {}) {
  if (!Array.isArray(rawRows)) return;

  const {
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    fileBase = 'ventas-por-hora',
    encabezado = null,
    encabezadoFallback = null,
    sucursalNombre = '',
    reporteTitulo = VENTAS_POR_HORA_TITULO_DEFAULT,
    apiTotales = null,
    columnas = null,
  } = options;

  const resolvedBase = resolveVentasPorHoraColumns(rawRows[0] ?? null);
  const resolved = applyVentasPorHoraColumnLabelsFromApi(resolvedBase, columnas);

  const normRows = rawRows.map((r) => rowToVentasPorHoraNorm(r, resolved));
  const summed = sumVentasPorHoraTotals(normRows, resolved);
  const totals = mergeVentasPorHoraFootTotals(
    summed,
    mapApiVentasPorHoraTotalesToNormIds(apiTotales),
    resolved
  );

  const encBase = mergeVentasPorHoraEncabezado(encabezado, encabezadoFallback);
  const sn = String(sucursalNombre || '').trim();
  if (sn && !String(encBase.nombreComercial || '').toUpperCase().includes(sn.toUpperCase())) {
    encBase.nombreComercial = encBase.nombreComercial
      ? `${encBase.nombreComercial} - ${sn.toUpperCase()}`
      : sn.toUpperCase();
  }
  const encMerged = withVentasPorHoraPeriodoLine(
    { ...encBase, reporteTitulo },
    fechaInicio,
    fechaFin
  );

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  let y = drawEncabezadoReportePdf(
    doc,
    encMerged,
    pageW,
    margin,
    margin,
    {
      fechaInicio,
      fechaFin,
      cajaInicio,
      cajaFin,
      turnoInicio,
      turnoFin,
    },
    {
      omitResponsable: true,
      omitDomicilio: false,
      tituloResaltadoRgb: [191, 219, 254],
      turnosEncabezadoEstilo: 'de',
    }
  );

  const head = [resolved.map((c) => c.label)];
  const body = normRows.map((r) => resolved.map((c) => fmtPdf(c, r[c.id])));

  const foot = [
    resolved.map((c) => {
      if (!c.total) return '';
      const n = totals[c.id];
      return n != null && !Number.isNaN(n) ? formatVentasPorHoraCell(c, n) : '—';
    }),
  ];

  const colW = [70, 170];
  const innerW = pdfTableInnerWidthMm(pageW, margin);
  const weights = resolved.map((_, i) => colW[i] ?? 1);
  const widths = columnWidthsProportionalMm(weights, innerW);

  autoTable(doc, {
    startY: y,
    tableWidth: innerW,
    head,
    body,
    foot,
    showFoot: 'lastPage',
    theme: 'plain',
    tableLineColor: [0, 0, 0],
    headStyles: {
      fillColor: TABLE_HEAD_BG,
      textColor: TABLE_HEAD_TEXT,
      fontStyle: 'bold',
      lineWidth: { top: 0.15, bottom: 0.25, left: 0.08, right: 0.08 },
      lineColor: [30, 58, 110],
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: { top: 0, bottom: 0.12, left: 0, right: 0 },
    },
    bodyStyles: {
      textColor: [0, 0, 0],
      lineWidth: { top: 0, bottom: 0, left: 0, right: 0 },
    },
    styles: { fontSize: 8, cellPadding: 1.3 },
    columnStyles: Object.fromEntries(
      resolved.map((c, i) => [i, { halign: c.align, cellWidth: widths[i] }])
    ),
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      const colIdx = data.column.index;
      if (colIdx >= resolved.length) return;
      const c = resolved[colIdx];
      if (data.section === 'head') {
        data.cell.styles.halign = c.align;
        data.cell.styles.fillColor = TABLE_HEAD_BG;
        data.cell.styles.textColor = TABLE_HEAD_TEXT;
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.section === 'foot') {
        data.cell.styles.halign = c.align;
        if (c.total) {
          data.cell.styles.lineWidth = { top: 0.45, bottom: 0.18, left: 0, right: 0 };
        } else {
          data.cell.styles.lineWidth = { top: 0, bottom: 0.18, left: 0, right: 0 };
        }
      }
    },
  });

  const totalPages = doc.internal.getNumberOfPages();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(90);
    doc.text(`Generado ${new Date().toLocaleString('es-MX')}`, margin, ph - 7);
    doc.text(`Página ${i} de ${totalPages}`, pageW - margin, ph - 7, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save(`${fileBase}-${fechaInicio || 'inicio'}-${fechaFin || 'fin'}.pdf`);
}
